import React, { useEffect, useState } from 'react';
import { Swiper, SwiperSlide } from 'swiper/react';
import { Navigation, Autoplay, EffectFade } from 'swiper/modules';
import 'swiper/css';
import 'swiper/css/effect-fade';
import 'swiper/css/navigation';
import { usePopup } from "../context/PopupContext";
import SRConsole from './SearchConsole/SRConsole';
import { Filters } from "@/pages/SearchResult/SearchPage";
import { heroSlidesData } from '@/data/heroSlidesData';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faLocationDot } from '@fortawesome/free-solid-svg-icons';

type HeroProps = {
    slides?: typeof heroSlidesData;
    location?: string;
    ctaButton?: boolean;
    useAsSlider?: boolean;
    title?: string;
    subtitle?: string;
    image?: string;
    useCarouselDefaultText?: boolean;
};

const Hero: React.FC<HeroProps> = (
    {
        slides,
        location,
        ctaButton,
        useAsSlider = true,
        title,
        subtitle,
        image,
        useCarouselDefaultText = true
    }) => {
    const { openPopup } = usePopup();
    const [filters, setFilters] = useState<Filters>({});
    const [actualSlides, setActualSlides] = useState(heroSlidesData);


    useEffect(() => {
        let updatedSlides = [...(slides ?? heroSlidesData)];

        if (image) {
            updatedSlides.unshift({
                image: image,
                alt: title ?? '',
                mobile: image,
                title: title ?? '',
                subtitle: subtitle ?? ''
            });
        }

        if (title) {
            updatedSlides = updatedSlides.map(slide => ({ ...slide, title }));
        }
        if (subtitle) {
            updatedSlides = updatedSlides.map(slide => ({ ...slide, subtitle }));
        }
        if (!useCarouselDefaultText && !subtitle) {
            updatedSlides = updatedSlides.map(slide => ({ ...slide, subtitle: '' }));
        }

        updatedSlides = updatedSlides.map(slide => ({
            ...slide,
            title: title !== undefined ? title : slide.title,
            subtitle: subtitle !== undefined ? subtitle : (useCarouselDefaultText ? slide.subtitle : ''),
        }));

        setActualSlides(updatedSlides);
    }, [slides, image, location, title, subtitle, useCarouselDefaultText]);

    return (
        <>
            <section className="hero">
                {location && (
                    <div className="location">
                        {/* <FontAwesomeIcon icon={faLocationDot} /> */}
                        {location}
                    </div>
                )}
                {useAsSlider ? (
                    <Swiper
                        className="swiper-hero"
                        loop
                        autoplay={{ delay: 5000, disableOnInteraction: false }}
                        effect="fade"
                        fadeEffect={{ crossFade: true }}
                        speed={1200}
                        navigation={{
                            nextEl: '.swiper-button-next',
                            prevEl: '.swiper-button-prev',
                        }}
                        modules={[Autoplay, Navigation, EffectFade]}
                    >
                        {actualSlides.map((slide, i) => (
                            <SwiperSlide key={slide.image || i}>
                                <picture>
                                    <source srcSet={slide.mobile} media="(max-width: 768px)" />
                                    <img src={slide.image} className="slide-image" alt={slide.alt} />
                                </picture>
                                <div className={`slide-content ${useCarouselDefaultText ? '' : 'static'}`}>
                                    <h1>{slide.title}</h1>
                                    <p>{slide.subtitle}</p>
                                    {ctaButton && <button className="cta black" onClick={openPopup} type="button">Explore now</button>}
                                </div>
                            </SwiperSlide>
                        ))}

                        <div className="swiper-button-prev">
                            <svg width="10" height="18" viewBox="0 0 10 18">
                                <path d="M1 1L9 9L1 17" stroke="currentColor" />
                            </svg>
                        </div>
                        <div className="swiper-button-next">
                            <svg width="10" height="18" viewBox="0 0 10 18">
                                <path d="M1 1L9 9L1 17" stroke="currentColor" />
                            </svg>
                        </div>
                    </Swiper>
                ) : (
                    <div className="hero-image-container">
                        <picture>
                            <source srcSet={image} media="(max-width: 768px)" />
                            <img src={image} alt={title} className="hero-image" />
                        </picture>
                        <div className="slide-content">
                            <h1>{title}</h1>
                            <p>{subtitle}</p>
                        </div>
                    </div>
                )}
                <SRConsole />
            </section>
        </>
    );
};

export default Hero;